/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { createApi } from "@reduxjs/toolkit/query/react"

import { FoodmaxapipbGetMerchantResponse } from "generated/foodmaxapi.type"
import {
  DELV_PLATFORM_FEATURE_KEYS,
  GetFeatureResponse,
} from "types/delv-platform-api"

import { axiosBaseQuery, createRequestHeaders } from "./apiClient"
import {
  apiReducerPaths,
  dpApiEndpoints,
  dpApiMerchantUrl,
  endpoints,
} from "./constants"
import { MerchantContext } from "./types"

const dpApiBaseQuery = axiosBaseQuery(dpApiMerchantUrl)

interface GetMerchantDetailRequest {
  merchantID: string
  /** When true, suppresses the full-page loading overlay. Cache key is based on merchantID only. */
  silent?: boolean
}

interface GetDpApiFeaturesRequest extends MerchantContext {
  featureKeys: DELV_PLATFORM_FEATURE_KEYS[]
  menuGroupID?: string
}

export const merchantApi = createApi({
  reducerPath: apiReducerPaths.merchantApi,
  baseQuery: axiosBaseQuery(),
  endpoints: builder => ({
    getMerchantDetail: builder.query<
      FoodmaxapipbGetMerchantResponse["merchant"],
      GetMerchantDetailRequest
    >({
      query: ({ merchantID, silent = false }) => ({
        url: endpoints.getMerchant,
        headers: createRequestHeaders({ merchantID }),
        ...(silent ? {} : { extraOptions: { showGlobalLoading: true } }),
      }),
      // Exclude `silent` from the cache key so silent and non-silent calls
      // share the same cached result.
      serializeQueryArgs: ({ queryArgs }) => ({
        merchantID: queryArgs.merchantID,
      }),
      keepUnusedDataFor: Infinity,
      transformResponse: (response: FoodmaxapipbGetMerchantResponse) => {
        return response.merchant
      },
    }),
    getDpApiFeatures: builder.query<
      Partial<GetFeatureResponse>,
      GetDpApiFeaturesRequest
    >({
      queryFn: async (
        { merchantID, merchantGroupID, ...params },
        queryApi,
        extraOptions,
      ) => {
        const result = await dpApiBaseQuery(
          {
            url: dpApiEndpoints.getDpApiFeatures,
            headers: createRequestHeaders({ merchantID, merchantGroupID }),
            params,
            extraOptions: { showGlobalLoading: true },
          },
          queryApi,
          extraOptions,
        )
        if (result.error) return { ...result }
        return { ...result, data: result.data as Partial<GetFeatureResponse> }
      },
    }),
  }),
})

export const {
  useGetMerchantDetailQuery,
  useGetDpApiFeaturesQuery,
  useLazyGetDpApiFeaturesQuery,
} = merchantApi
